import { EntryType, InvoiceQueryFilters } from 'openapi';

import {
  ALL_COMPANY_INVOICES_FILTER_FIELDS,
  INITIAL_INVOICES_FILTERS,
  INVOICES_FILTER_FIELDS,
  INVOICES_FILTERS_DEFAULT_VALUES
} from 'utils/constants/invoices';
import {
  ALL_COMPANY_REVENUE_FILTER_FIELDS,
  INITIAL_REVENUE_FILTERS,
  REVENUE_FILTER_FIELDS,
  REVENUE_FILTERS_DEFAULT_VALUES
} from 'utils/constants/revenue';
import { FilterType } from 'utils/enums/FilterType';
import { DocumentParticipantType } from 'utils/enums/Invoice';

export interface ActionPermissions {
  canApprove: boolean;
  canReject: boolean;
  canPay: boolean;
  canBook: boolean;
  canDelete: boolean;
  canUpload: boolean;
  canCreate: boolean;
  canSettle: boolean;
}

export interface DocumentPermissions {
  INVOICE_APPROVAL?: { update?: boolean };
  INVOICE_PAYMENTS?: { create?: boolean };
  INVOICE_ACCOUNTING?: { update?: boolean };
  INVOICE_SETTLEMENTS?: { create?: boolean };
}

export interface FilterField {
  name: string;
  translationKey: string;
}

export interface DocumentConfig {
  initialFilters: InvoiceQueryFilters;
  entryType: EntryType;
  filtersFields: FilterField[];
  allCompaniesFiltersFields: FilterField[];
  defaultFilterValues: Record<string, unknown>;
  getActionPermissions: (params: {
    filterType: FilterType;
    permissions: DocumentPermissions;
  }) => ActionPermissions;
  allowReimbursement: boolean;
  allowExpenseType: boolean;
  allowPrepaid: boolean;
  showPaymentStatus: boolean;
  getParticipant: () => DocumentParticipantType;
  allowFilterFields: {
    allowAccounted: boolean;
    allowPaymentStatus: boolean;
    allowReimbursement: boolean;
  };
  labels: {
    noIbans: string;
  };
}

const getInvoiceActionPermissions = ({
  filterType,
  permissions
}: {
  filterType: FilterType;
  permissions: DocumentPermissions;
}): ActionPermissions => {
  const isInitial = filterType === FilterType.Initial;
  const isApproval = filterType === FilterType.Approval;
  const isPayment = filterType === FilterType.Payment;
  const isAccounting = filterType === FilterType.Accounting;

  return {
    canApprove:
      (permissions.INVOICE_APPROVAL?.update ?? false) &&
      (isApproval || isInitial),
    canReject:
      (permissions.INVOICE_APPROVAL?.update ?? false) &&
      (isApproval || isInitial),
    canPay:
      (permissions.INVOICE_PAYMENTS?.create ?? false) &&
      (isPayment || isInitial),
    canBook:
      (permissions.INVOICE_ACCOUNTING?.update ?? false) &&
      (isAccounting || isInitial),
    canDelete: isInitial,
    canUpload: isInitial,
    canCreate: isInitial,
    canSettle: false
  };
};

const getRevenueActionPermissions = ({
  permissions
}: {
  filterType: FilterType;
  permissions: DocumentPermissions;
}): ActionPermissions => ({
  canApprove: false,
  canReject: false,
  canPay: false,
  canBook: false,
  canDelete: true,
  canUpload: true,
  canCreate: true,
  canSettle: permissions.INVOICE_SETTLEMENTS?.create ?? false
});

export const DOCUMENT_CONFIGS: Record<EntryType, DocumentConfig> = {
  [EntryType.EXPENSE]: {
    initialFilters: INITIAL_INVOICES_FILTERS,
    entryType: EntryType.EXPENSE,
    filtersFields: INVOICES_FILTER_FIELDS,
    allCompaniesFiltersFields: ALL_COMPANY_INVOICES_FILTER_FIELDS,
    defaultFilterValues: INVOICES_FILTERS_DEFAULT_VALUES,
    getActionPermissions: getInvoiceActionPermissions,
    allowReimbursement: true,
    allowExpenseType: true,
    allowPrepaid: true,
    showPaymentStatus: true,
    allowFilterFields: {
      allowAccounted: true,
      allowPaymentStatus: true,
      allowReimbursement: true
    },
    getParticipant: () => DocumentParticipantType.RECEIVER,
    labels: {
      noIbans: 'messages.noInvoiceIbans'
    }
  },
  [EntryType.REVENUE]: {
    initialFilters: INITIAL_REVENUE_FILTERS,
    entryType: EntryType.REVENUE,
    filtersFields: REVENUE_FILTER_FIELDS,
    allCompaniesFiltersFields: ALL_COMPANY_REVENUE_FILTER_FIELDS,
    defaultFilterValues: REVENUE_FILTERS_DEFAULT_VALUES,
    getActionPermissions: getRevenueActionPermissions,
    allowReimbursement: false,
    allowExpenseType: false,
    allowPrepaid: false,
    showPaymentStatus: false,
    allowFilterFields: {
      allowAccounted: false,
      allowPaymentStatus: false,
      allowReimbursement: false
    },
    getParticipant: () => DocumentParticipantType.ISSUER,
    labels: {
      noIbans: 'messages.noRevenueIbans'
    }
  }
};

export function getConfigByDocumentType(
  documentType: EntryType
): DocumentConfig {
  return DOCUMENT_CONFIGS[documentType] ?? DOCUMENT_CONFIGS[EntryType.EXPENSE];
}
